import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  Portal,
  Box,
  chakra,
  Flex,
  Icon,
  Skeleton,
  StackDirection,
  Text,
  useToken,
} from "@chakra-ui/react";
import { LayoutGroup, motion } from "framer-motion";
import type { IconType } from "react-icons";
import { FiChevronUp, FiFileText, FiGrid, FiLifeBuoy, FiLogOut } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./auth/context";
import { CustomerDetails } from "./appWrapper";
import { AvatarInitials, DefinitionList } from "./ui";
import { duration, ease, reducedTransition, spring, useReducedMotion } from "../theme";


interface NavItem {
  path: string;
  label: string;
  icon: IconType;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/", label: "Dashboard", icon: FiGrid },
  { path: "/contracts", label: "My Contracts", icon: FiFileText },
  { path: "/tickets", label: "Help and Support", icon: FiLifeBuoy },
];

interface UserPanelProps {
  userData: CustomerDetails | null;
  onLogout: () => void;
}

interface NavListProps {
  direction: StackDirection;
  CloseAfterSelect: () => void;
  userData: CustomerDetails | null;
  isPending: boolean;
}

const mailto = (email?: string | null) => (email ? `mailto:${email}` : undefined);

/** Popover body: navy identity band → account + Bidfood team definition lists → Log out. */
const UserPanel: React.FC<UserPanelProps> = ({ userData, onLogout }) => {
  const name = userData?.name.value ?? "";

  return (
    <>
      <Flex align="center" gap={4} p={5} bg="navy.900" color="white" borderTopRadius="lg">
        {/* Name is visible right next to the avatar, so the avatar's img label would be read twice. */}
        <AvatarInitials name={name} size={48} ring aria-hidden />
        <Box minW={0}>
          <Text as="p" textStyle="h3" color="white" noOfLines={1}>
            {name}
          </Text>
          {userData?.subsidiary.text && (
            <Text textStyle="caption" color="navy.200" noOfLines={1}>
              {userData.subsidiary.text}
            </Text>
          )}
          <Box
            as="span"
            display="inline-block"
            mt={2}
            textStyle="label"
            bg="leaf.500"
            color="navy.900"
            borderRadius="full"
            px={2}
            py={0.5}
          >
            ACTIVE CUSTOMER
          </Box>
        </Box>
      </Flex>

      <PopoverBody p={5} maxH="60vh" overflowY="auto" textAlign="start">
        <DefinitionList
          eyebrow="YOUR ACCOUNT"
          items={[
            { label: "Customer", value: userData?.name.value },
            // Spec §5.2 shows the branch here (subsidiary sits in the header band); fall back so a null NetSuite text never shows "—".
            { label: "Branch", value: userData?.branch.text || userData?.subsidiary.text },
          ]}
        />
        <DefinitionList
          mt={5}
          eyebrow="YOUR BIDFOOD TEAM"
          columns={2}
          items={[
            { label: "BDE", value: userData?.bde.value },
            { label: "BDE Email", value: userData?.bdeEmail.value, href: mailto(userData?.bdeEmail.value) },
            { label: "Sales Rep", value: userData?.salesRep.value },
            { label: "Sales Rep Email", value: userData?.SalesRepEmail.value, href: mailto(userData?.SalesRepEmail.value) },
            { label: "Customer Rep", value: userData?.customerRep.value },
            { label: "Customer Rep Email", value: userData?.customerRepEmail.value, href: mailto(userData?.customerRepEmail.value) },
          ]}
        />
      </PopoverBody>

      <PopoverFooter p={4}>
        <Button variant="danger-soft" w="full" leftIcon={<FiLogOut />} onClick={onLogout}>
          Log out
        </Button>
      </PopoverFooter>
    </>
  );
};

export const NavList = ({ direction, CloseAfterSelect, userData, isPending }: NavListProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;

  const { unauthorize } = useAuth();

  const reduce = useReducedMotion();
  const [pillBg] = useToken("colors", ["leaf.500"]);
  const [pillShadow] = useToken("shadows", ["insetNav"]);

  return (
    <Flex direction={direction} flex={1} minH={0} color="white" w="full">
      <Text as="p" textStyle="label" color="navy.300" px={6} mt={6} mb={2}>
        MENU
      </Text>

      <Box as="nav" aria-label="Primary">
        <LayoutGroup id="primary-nav">
          <Box as="ul" listStyleType="none" m={0} p={0} display="flex" flexDirection="column" gap={1}>
            {NAV_ITEMS.map((item, index) => {
              const active = isActive(item.path);
              return (
                <motion.li
                  key={item.path}
                  style={{ listStyle: "none", margin: "0 0.75rem" }}
                  initial={reduce ? false : { opacity: 0, x: -6 }}
                  animate={{ opacity: 1, x: 0 }}
                  // Gestures carry their own fast transitions; the staggered delay below applies to the mount only.
                  whileHover={reduce ? undefined : { x: 2, transition: { duration: duration.fast, ease: ease.standard } }}
                  whileTap={reduce ? undefined : { scale: 0.97, transition: { duration: duration.instant } }}
                  transition={
                    reduce ? reducedTransition : { duration: duration.slow, ease: ease.standard, delay: index * 0.04 }
                  }
                >
                  <Button
                    variant="nav"
                    aria-current={active ? "page" : undefined}
                    onClick={() => { navigate(item.path); CloseAfterSelect(); }}
                    sx={{ "&:hover .mbf-nav-icon": { color: active ? "navy.900" : "white" } }}
                  >
                    {active && (
                      <motion.span
                        layoutId="nav-pill"
                        aria-hidden
                        style={{
                          display: "block",
                          position: "absolute",
                          inset: 0,
                          borderRadius: 9999,
                          background: pillBg,
                          boxShadow: pillShadow,
                        }}
                        transition={reduce ? reducedTransition : spring.snappy}
                      />
                    )}
                    <Icon
                      as={item.icon}
                      className="mbf-nav-icon"
                      boxSize="20px"
                      position="relative"
                      zIndex={1}
                      color={active ? "navy.900" : "navy.300"}
                      transition={`color ${duration.instant * 1000}ms`}
                      aria-hidden
                    />
                    <Box as="span" position="relative" zIndex={1}>
                      {item.label}
                    </Box>
                  </Button>
                </motion.li>
              );
            })}
          </Box>
        </LayoutGroup>
      </Box>

      {isPending ? (
        <Skeleton speed={1.2} mt="auto" mx={3} mb={4} h="64px" borderRadius="lg" startColor="navy.800" endColor="navy.700" />
      ) : (
        <Box mt="auto" mx={3} mb={4}>
          <Popover placement="top-start" isLazy>
            {({ isOpen }) => (
              <>
                <PopoverTrigger>
                  <chakra.button
                    type="button"
                    display="flex"
                    alignItems="center"
                    gap={3}
                    w="full"
                    p={3}
                    textAlign="left"
                    borderRadius="lg"
                    bg="rgba(255,255,255,0.06)"
                    border="1px solid"
                    borderColor="border.onDark"
                    cursor="pointer"
                    transition={`background-color ${duration.fast * 1000}ms`}
                    _hover={{ bg: "rgba(255,255,255,0.10)" }}
                    _focusVisible={{ boxShadow: "outlineOnDark", outline: "none" }}
                  >
                    {/* Spans only: a <button> allows phrasing content. */}
                    <AvatarInitials as="span" name={userData?.name.value ?? ""} size={40} ring aria-hidden />
                    <Box as="span" display="block" minW={0} flex={1}>
                      <Text as="span" textStyle="body.sm" fontWeight={700} color="white" noOfLines={1}>
                        {userData?.name.value}
                      </Text>
                      <Text as="span" textStyle="caption" color="navy.200" noOfLines={1} wordBreak="break-all">
                        {userData?.bdeEmail.value}
                      </Text>
                    </Box>
                    <Icon
                      as={FiChevronUp}
                      boxSize="16px"
                      color="navy.300"
                      flexShrink={0}
                      transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
                      transition={`transform ${duration.normal * 1000}ms`}
                      aria-hidden
                    />
                  </chakra.button>
                </PopoverTrigger>
                {/* Portal: the sidebar column and the Drawer both clip overflow, and this panel is wider/taller than the column. */}
                <Portal>
                  <PopoverContent w="min(380px, calc(100vw - 2rem))" overflow="hidden" aria-label="Your account">
                    <UserPanel userData={userData} onLogout={() => { unauthorize(); navigate("/"); }} />
                  </PopoverContent>
                </Portal>
              </>
            )}
          </Popover>
        </Box>
      )}
    </Flex>
  );
};
